import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl, useIntl } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import intlShape from '@/shared/utils/intlShape';
import classNames from 'classnames';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import InputGroupWrapper from '@/shared/components/InputGroupWrapper';
import messages from './emailMessages';

const searchFunc = (item, searchValue) =>
  item && typeof item === 'string' && item.toLowerCase().includes(searchValue.toLowerCase());

const searchPlaceholders = (items, searchValue, getter = x => x) => {
  if (!items || items.length === 0) return [];
  if (!searchValue) return items;

  const terms = searchValue.split(' ');
  for (let i = 0; i < terms.length; i++) {
    const term = terms[i];
    items = items.filter(x => searchFunc(getter(x), term));
  }

  return items;
};
const searchKeyGetter = x => x?.searchKey;

class PlaceholderModal extends Component {
  state = {
    placeholders: [],
    taskPlaceholders: [],
    searchValue: '',
  };

  componentDidMount() {
    this.retrievePlaceholders();
  }

  static propTypes = {
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    templateContext: PropTypes.number.isRequired,
  };

  retrievePlaceholders = () => {
    const { templateContext, intl } = this.props;
    if (!templateContext) {
      console.warn('No templatecontext was passed to PlaceholderModal');
      return;
    }
    const normalPlaceholdersPromise = api.base.get(
      `${config.appUrls.api}/api/template/MailPlaceholders`,
    );
    const taskPlaceholdersPromise =
      templateContext === 11
        ? api.base.get(`${config.appUrls.tasks}/api/taskparameters/placeholders`)
        : { data: [] };

    Promise.all([normalPlaceholdersPromise, taskPlaceholdersPromise]).then(
      ([{ data: normalPlaceholders }, { data: taskPlaceholders }]) => {
        this.setState({
          placeholders: normalPlaceholders[templateContext]?.map(x => {
            const description = intl.formatMessage(listItemMsg('mailplaceholder', x, ''));
            return {
              placeholder: x,
              description,
              searchKey: `${x};${description}`,
            };
          }),
          taskPlaceholders: taskPlaceholders?.map(x => {
            const label = intl.formatMessage(
              listItemMsg('tasktemplate', x.taskTemplateId, x.taskTemplateName),
            );
            return {
              ...x,
              label,
              searchKey: `${label};${x.placeholders?.join(';')}`,
            };
          }),
        });
      },
    );
  };

  render() {
    const { intl, onClose } = this.props;
    const { placeholders, taskPlaceholders, searchValue } = this.state;
    const hasTaskPlaceholders = taskPlaceholders.length > 0;
    const filteredPlaceholders = searchPlaceholders(placeholders, searchValue, searchKeyGetter);
    const filteredTaskPlaceholders = searchPlaceholders(
      taskPlaceholders,
      searchValue,
      searchKeyGetter,
    );

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.Placeholders)}
        show
        onClose={onClose}
        onSave={onClose}
        dismissOnClickOutsideModal
        size="lg"
      >
        <div className="container-fluid">
          <div className="row margin10B">
            <div className="col-md-12">
              <FormattedMessage {...messages.UseThesePlaceholdersInYourEmail} />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <InputGroupWrapper
                rightClassName="input-group-btn"
                right={
                  searchValue ? (
                    <button
                      type="button"
                      onClick={() => this.setState({ searchValue: '' })}
                      className="btn btn-default"
                    >
                      <i className="fa fa-times" />
                    </button>
                  ) : null
                }
              >
                <input
                  type="text"
                  value={searchValue}
                  onChange={e => this.setState({ searchValue: e.target.value })}
                  autoComplete="off"
                  autoFocus
                  data-1p-ignore
                  className="form-control"
                  placeholder={intl.formatMessage(messages.Search)}
                />
              </InputGroupWrapper>
            </div>
          </div>
          <div className="row">
            <div
              className={classNames({
                'col-md-12': !hasTaskPlaceholders,
                'col-md-8': hasTaskPlaceholders,
              })}
            >
              {filteredPlaceholders.map(({ placeholder, description }, i) => (
                <div
                  key={i}
                  className={classNames('row', {
                    margin10B:
                      filteredPlaceholders[i + 1]?.placeholder &&
                      placeholder.charAt(1) !== filteredPlaceholders[i + 1].placeholder.charAt(1),
                  })}
                >
                  <div className="col-md-6 break-word">{placeholder}</div>
                  <div className="col-md-6 break-word">{description}</div>
                </div>
              ))}
            </div>
            {hasTaskPlaceholders && (
              <div className="col-md-4">
                {filteredTaskPlaceholders.map(taskParameter => {
                  const filtered = searchPlaceholders(taskParameter.placeholders, searchValue);

                  return (
                    <div className="margin10B" key={taskParameter.taskTemplateId}>
                      <span className="bold">{taskParameter.label}</span>
                      {(filtered.length === 0 ? taskParameter.placeholders : filtered)?.map(
                        placeholder => (
                          <div key={placeholder}>{placeholder}</div>
                        ),
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </OkCancelModal>
    );
  }
}

export default injectIntl(PlaceholderModal);
